import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Apple, Play } from 'lucide-react';
import { brand, navLinks } from '../mock';

function FooterBrand() {
  return (
    <div className="md:col-span-2">
      <Link to="/" className="flex items-center gap-3 group w-fit">
        <img
          src="/brand/app-icon.png"
          alt="LISTING AiGENT logo"
          width="44"
          height="44"
          className="w-11 h-11 rounded-full object-cover"
        />
        <span className="font-serif text-brand-cream text-xl tracking-wide">
          LISTING <span className="relative">A<span className="text-brand-gold">i</span>GENT</span>
        </span>
      </Link>
      <p className="mt-6 text-brand-cream/70 max-w-md leading-relaxed">
        {brand.tagline} Built for realtors who want to close more deals and answer fewer calls.
      </p>

      <div className="mt-8 space-y-3 text-sm text-brand-cream/70">
        <div className="flex items-start gap-3">
          <Mail className="w-4 h-4 mt-0.5 text-brand-gold" />
          <a href={`mailto:${brand.email}`} className="hover:text-brand-cream transition-colors">
            {brand.email}
          </a>
        </div>
        <div className="flex items-start gap-3">
          <MapPin className="w-4 h-4 mt-0.5 text-brand-gold" />
          <span>{brand.address}</span>
        </div>
      </div>
    </div>
  );
}

function FooterCompanyLinks() {
  return (
    <div>
      <h4 className="font-serif text-lg mb-5 text-brand-cream">Company</h4>
      <ul className="space-y-3 text-sm">
        {navLinks.map((link) => (
          <li key={link.to}>
            <Link to={link.to} className="text-brand-cream/70 hover:text-brand-cream transition-colors">
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function StoreDownloadButton({ icon: Icon, label, href, testId }) {
  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={testId}
        className="flex items-center gap-2 px-3 py-2 rounded-md border border-brand-cream/40 text-xs text-brand-cream hover:bg-brand-cream hover:text-brand-green transition-colors"
      >
        <Icon className="w-4 h-4" />
        <span>{label}</span>
      </a>
    );
  }
  return (
    <button
      type="button"
      disabled
      aria-disabled="true"
      data-testid={testId}
      className="flex items-center gap-2 px-3 py-2 rounded-md border border-brand-cream/20 text-xs text-brand-cream/80 opacity-70 cursor-not-allowed"
    >
      <Icon className="w-4 h-4" />
      <span>{label}</span>
    </button>
  );
}

function FooterLegalAndDownloads() {
  return (
    <div>
      <h4 className="font-serif text-lg mb-5 text-brand-cream">Legal</h4>
      <ul className="space-y-3 text-sm">
        <li>
          <Link to="/terms" className="text-brand-cream/70 hover:text-brand-cream transition-colors">
            Terms of Service
          </Link>
        </li>
        <li>
          <Link to="/privacy" className="text-brand-cream/70 hover:text-brand-cream transition-colors">
            Privacy Policy
          </Link>
        </li>
        <li>
          <Link to="/sms-policy" className="text-brand-cream/70 hover:text-brand-cream transition-colors">
            SMS Policy
          </Link>
        </li>
        <li>
          <Link to="/dmca" data-testid="footer-dmca-link" className="text-brand-cream/70 hover:text-brand-cream transition-colors">
            DMCA / Copyright
          </Link>
        </li>
        <li>
          <Link to="/opt-out" className="text-brand-cream/70 hover:text-brand-cream transition-colors">
            Call &amp; Text Opt-Out
          </Link>
        </li>
        <li>
          <a href="/delete-account" className="text-brand-cream/70 hover:text-brand-cream transition-colors">
            Delete Account
          </a>
        </li>
        <li>
          <a href="mailto:contact@listingaigent.com" className="text-brand-cream/70 hover:text-brand-cream transition-colors">
            contact@listingaigent.com
          </a>
        </li>
      </ul>

      <h4 className="font-serif text-lg mt-8 mb-4 text-brand-cream">Download</h4>
      <div className="flex flex-col gap-2">
        <StoreDownloadButton
          icon={Apple}
          label="App Store — Download Now"
          href="https://apps.apple.com/app/listing-aigent/id6789615649"
          testId="footer-appstore"
        />
        <StoreDownloadButton
          icon={Play}
          label="Google Play — Download Now"
          href="https://play.google.com/store/apps/details?id=com.listingaigent.app"
          testId="footer-playstore"
        />
      </div>
    </div>
  );
}

function FooterCopyright({ year }) {
  return (
    <div className="mt-14 pt-8 border-t border-brand-cream/10 flex items-center justify-between gap-4">
      <p className="text-xs text-brand-cream/50">
        © {year} {brand.legal}. All rights reserved.
      </p>
    </div>
  );
}

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="bg-brand-green-darker text-brand-cream mt-24">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          <FooterBrand />
          <FooterCompanyLinks />
          <FooterLegalAndDownloads />
        </div>
        <FooterCopyright year={year} />
      </div>
    </footer>
  );
}
